import { MenuItem } from "@blueprintjs/core"
import Divider from "components/Atoms/Divider"
import Note from "components/Atoms/Note"
import StaticSelector from "components/Atoms/StaticSelector"
import Toggle from "components/Atoms/Toggle"
import CustomGoogleMap from "components/Organisms/map/CustomGoogleMap"
import MapControl from "components/Organisms/map/MapControl"
import AllDCLocations from "components/Templates/AllDCLocations"
import { COLOURS } from "data/static.items"
import { useAllDcLocationsQuery } from "generated/graphql"
import useAuth from "hooks/useAuth"
import { isArray } from "lodash-es"
import React, { useMemo, useRef, useState } from "react"
import { gisToJson, jsonToGis, pointToJson } from "utils/map.utils"
import { Locations } from "utils/symbols.utils"
import shallow from "zustand/shallow"
import AreaPolygon from "../AreaPolygon"
import styles from "../dc.module.scss"
import useDCStore from "../dc.store"
import FormLayout from "../FormLayout"
const DCAreasServed = ({ disabled = false }) => {
  const auth = useAuth()
  const mapRef = useRef<any>()

  const { geometry, onAdd, color, onChange } = useDCStore(
    store => ({
      geometry: store.state.basicDetails.geometry,
      onAdd: store.addListItem,
      color: store.state.basicDetails.colorCode,
      onChange: store.setData("basicDetails"),
    }),
    shallow
  )

  const [isSticky, setSticky] = useState(true)
  const [center, setCenter] = useState(Locations.Mohadi)
  const { data } = useAllDcLocationsQuery({
    variables: { pocId: auth?.user?.id },
  })
  const dcs = data?.allDistributionCentreLocations

  const allPoints = useMemo(
    () =>
      dcs?.reduce((prev, dc) => {
        const coords = []
        for (const geometry of dc?.geometry?.coordinates?.[0] ?? []) {
          const jsonPoints = gisToJson(geometry)
          coords.push(jsonPoints)
        }
        return prev.concat(...coords)
      }, []),
    [dcs]
  )

  return (
    <>
      <FormLayout className={styles.grid} title="Areas Served">
        <StaticSelector
          onChange={onChange}
          value={color}
          icon={
            <span
              style={{
                borderRadius: 4,
                background: color?.id || color,
                height: 14,
                width: 14,
              }}
            />
          }
          itemRenderer={(item, { handleClick, modifiers }) => {
            return (
              <MenuItem
                icon={
                  <span
                    style={{
                      borderRadius: 4,
                      background: item?.id,
                      height: 15,
                      width: 15,
                    }}
                  />
                }
                active={modifiers.active}
                key={item.id}
                onClick={handleClick}
                text={item?.name}
              />
            )
          }}
          label="Color"
          path="colorCode"
          items={COLOURS}
        />
        <Note style={{ gridColumn: "2/4" }}>
          <span className="font-700">Please Note: </span>All polygons under this
          DC will be colored in this shade.
        </Note>
      </FormLayout>
      <Divider className="my-0" />
      <FormLayout
        title="Create Map"
        subtitle="The boundaries created here will allow users to place orders in this areas only ."
        padding={null}
      >
        <CustomGoogleMap
          drawingMode={disabled ? null : ["polygon"]}
          onLoad={map => {
            mapRef.current = map
          }}
          center={center}
          onChange={(
            _: any,
            center: React.SetStateAction<{ lat: number; lng: number }>
          ) => {
            setCenter(center)
          }}
          onPolyComplete={e => {
            const gis = jsonToGis(e.coordinates)

            onAdd(`basicDetails.geometry.coordinates`, [[gis]])
          }}
          onCenterMapClicked={() => {
            const bounds = new window.google.maps.LatLngBounds()
            if (isArray(geometry?.coordinates)) {
              for (const poly of geometry?.coordinates) {
                for (const point of poly?.[0]) {
                  bounds.extend(pointToJson(point))
                }
              }
              return mapRef.current?.fitBounds(bounds)
            }
          }}
        >
          {geometry?.coordinates?.map((c: any[], idx: any) => {
            return (
              <AreaPolygon
                isSticky={isSticky}
                disabled={disabled}
                allDcVertices={allPoints}
                index={idx}
                key={idx}
                coordinates={gisToJson(c?.[0])}
              />
            )
          })}
          <AllDCLocations distributionCentres={dcs} />
          <MapControl position="BOTTOM_CENTER">
            <div className="bg-gray">
              <Toggle
                className="mb-6 ml-3"
                large
                label="Sticky"
                checked={isSticky}
                onClick={() => setSticky(prev => !prev)}
              />
            </div>
          </MapControl>
        </CustomGoogleMap>
      </FormLayout>
    </>
  )
}

export default DCAreasServed
